import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, FileText, Code, LifeBuoy, HelpCircle } from "lucide-react";

// Mirrors DocSearchDialog's ResultTypeIcon — kept inline here to
// avoid pulling a dialog-coupled component into the shell bundle.
function ResultTypeIcon({ sectionType, active }: { sectionType: string | undefined; active: boolean }) {
  const cls = `size-4 shrink-0 mt-0.5 ${active ? "text-indigo-500" : "text-slate-400"}`;
  switch (sectionType) {
    case "api":
      return <Code className={cls} />;
    case "support":
      return <LifeBuoy className={`size-4 shrink-0 mt-0.5 ${active ? "text-sky-500" : "text-sky-400"}`} />;
    case "faq":
      return <HelpCircle className={`size-4 shrink-0 mt-0.5 ${active ? "text-violet-500" : "text-violet-400"}`} />;
    default:
      return <FileText className={cls} />;
  }
}
import { useProject } from "../../contexts/ProjectContext";
import { authFetch } from "../../utils/authFetch";

interface InlineSearchProps {
  /** Base path prefix, e.g. "/docs" or "/studio" */
  appPrefix: string;
  /** If true, uses the published search endpoint; otherwise CMS endpoint */
  readOnly?: boolean;
  /** Max width CSS class for the container */
  maxWidth?: string;
}

interface SearchResult {
  id: string;
  title: string;
  snippet: string;
  slugPath: string;
  sectionType?: string;
}

export default function InlineSearch({ appPrefix, readOnly = false, maxWidth = "max-w-[480px]" }: InlineSearchProps) {
  const navigate = useNavigate();
  const { currentProject } = useProject();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  // ⌘K to focus
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Escape to close/blur
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape" && open) {
        setOpen(false);
        inputRef.current?.blur();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open]);

  // Debounced search
  useEffect(() => {
    if (!query.trim() || !currentProject) { setResults([]); setOpen(false); setLoading(false); return; }
    // An in-flight response for an older query must not overwrite (or reopen) the current one.
    let stale = false;
    setLoading(true);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      try {
        const endpoint = readOnly
          ? `/api/docs/${currentProject.slug}/search?q=${encodeURIComponent(query)}`
          : `/api/projects/${currentProject.slug}/studio/doc-search?q=${encodeURIComponent(query)}`;
        const res = await authFetch(endpoint);
        if (res.ok) { const data = await res.json(); if (stale) return; setResults(data); setActiveIdx(0); setOpen(true); }
      } catch { if (!stale) setResults([]); }
      finally { if (!stale) setLoading(false); }
    }, 250);
    return () => { stale = true; clearTimeout(timerRef.current); };
  }, [query, currentProject, readOnly]);

  const go = useCallback((r: SearchResult) => {
    if (currentProject) {
      navigate(`${appPrefix}/projects/${currentProject.slug}/${r.slugPath}`);
    }
    setOpen(false);
    setQuery("");
    inputRef.current?.blur();
  }, [navigate, appPrefix, currentProject]);

  const onKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx(i => Math.min(i + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx(i => Math.max(i - 1, 0)); }
    else if (e.key === "Enter" && results[activeIdx]) { e.preventDefault(); go(results[activeIdx]); }
  }, [results, activeIdx, go]);

  return (
    <div ref={containerRef} className={`relative w-full min-w-[160px] ${maxWidth}`}>
      <div className="relative flex items-center">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-3.5 text-slate-400 pointer-events-none" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={e => setQuery(e.target.value)}
          onFocus={() => { if (results.length > 0) setOpen(true); }}
          onKeyDown={onKeyDown}
          placeholder="Search docs…"
          className="w-full py-1.5 text-[13px] rounded-lg border border-slate-300 bg-white/80 text-slate-700 placeholder:text-slate-400 outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-300 transition-colors"
          style={{ paddingLeft: 32, paddingRight: query ? 32 : 48 }}
        />
        {query ? (
          <button onClick={() => { setQuery(""); setResults([]); setOpen(false); }} className="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5 text-slate-400 hover:text-slate-600">
            <X className="size-3.5" />
          </button>
        ) : (
          <kbd className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] text-slate-400 px-1.5 py-0.5 rounded border border-slate-200 bg-slate-50 pointer-events-none">⌘K</kbd>
        )}
      </div>

      {/* Dropdown results */}
      {open && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-xl max-h-[400px] overflow-y-auto z-50">
          {loading && results.length === 0 && (
            <div className="px-4 py-6 text-center text-[13px] text-slate-400">Searching...</div>
          )}
          {!loading && query.trim() && results.length === 0 && (
            <div className="px-4 py-6 text-center text-[13px] text-slate-400">No results found</div>
          )}
          {results.map((r, i) => (
            <button
              key={r.id}
              type="button"
              onClick={() => go(r)}
              onMouseEnter={() => setActiveIdx(i)}
              className={`w-full text-left px-4 py-2.5 flex items-start gap-3 transition-colors ${
                i === activeIdx ? "bg-indigo-50" : "hover:bg-slate-50"
              }`}
            >
              <ResultTypeIcon sectionType={r.sectionType} active={i === activeIdx} />
              <div className="min-w-0 flex-1">
                <div className={`text-[13px] font-medium truncate ${i === activeIdx ? "text-indigo-700" : "text-slate-800"}`}>{r.title}</div>
                <div
                  className="text-[12px] text-slate-500 mt-0.5 line-clamp-1 [&_mark]:bg-amber-200/60 [&_mark]:text-slate-800 [&_mark]:rounded-sm [&_mark]:px-0.5"
                  dangerouslySetInnerHTML={{ __html: r.snippet }}
                />
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
